// Toggle visibility, edit labels, AND drag-reorder top-level pages (Home,
// Pathways, Glossary, Bookmarks, Tools and Metrics). All three settings live
// in the same `page_visibility` site-setting doc.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff, MonitorSmartphone, RotateCcw, Check, GripVertical } from "lucide-react";
import { usePageVisibilityBundle, saveVisibility, saveLabels, saveOrder } from "@/lib/pageVisibility";

const PAGE_META = {
  home:              { defaultLabel: "Home",              path: "/" },
  pathways:          { defaultLabel: "Pathways",          path: "/pathways" },
  glossary:          { defaultLabel: "Glossary",          path: "/glossary" },
  bookmarks:         { defaultLabel: "Bookmarks",         path: "/bookmarks" },
  tools_and_metrics: { defaultLabel: "Tools & Metrics",   path: "/tools-and-metrics" },
};

export default function PageVisibilityPanel() {
  const bundle = usePageVisibilityBundle();
  const [localVis, setLocalVis] = useState(null);
  const [localLabels, setLocalLabels] = useState(null);
  const [localOrder, setLocalOrder] = useState(null);
  const [savedFlash, setSavedFlash] = useState({});
  const debounceRefs = useRef({});

  useEffect(() => {
    if (localVis === null) setLocalVis(bundle.visibility);
    if (localLabels === null) setLocalLabels(bundle.labels);
    if (localOrder === null) setLocalOrder(bundle.order);
  }, [bundle, localVis, localLabels, localOrder]);

  const vis = localVis || bundle.visibility;
  const labels = localLabels || bundle.labels;
  const order = localOrder || bundle.order;

  const toggle = async (key) => {
    const next = { ...vis, [key]: !vis[key] };
    setLocalVis(next);
    try {
      await saveVisibility(next);
      toast.success(`${labels[key] || key} is now ${next[key] ? "visible" : "hidden"}`);
    } catch (e) {
      setLocalVis(vis);
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const editLabel = (key, value) => {
    setLocalLabels({ ...labels, [key]: value });
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    debounceRefs.current[key] = setTimeout(() => commitLabel(key, value), 700);
  };

  const commitLabel = async (key, value) => {
    try {
      await saveLabels({ [key]: value });
      setSavedFlash(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setSavedFlash(prev => ({ ...prev, [key]: false })), 1400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save label");
    }
  };

  const resetLabel = (key) => editLabel(key, PAGE_META[key].defaultLabel);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const src = result.source.index, dst = result.destination.index;
    if (src === dst) return;
    const next = Array.from(order);
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);
    setLocalOrder(next);
    try {
      await saveOrder(next);
      toast.success("Nav order updated");
    } catch (e) {
      setLocalOrder(order);
      toast.error(e.response?.data?.detail || "Could not save order");
    }
  };

  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <MonitorSmartphone className="w-4 h-4 text-sky-700"/>
          Page visibility, labels &amp; order
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-5">
          Drag <GripVertical className="w-3 h-3 inline-block align-text-bottom"/> to reorder pages in the site header — the order here is the order readers see. Rename any page (e.g. "Tools &amp; Metrics" → "Clinical Toolkit") or toggle it off entirely. Admins still see hidden pages via the URL with a "Hidden from public" banner.
        </p>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="page-visibility-list">
            {(dropProvided) => (
              <ul {...dropProvided.droppableProps} ref={dropProvided.innerRef}
                  className="divide-y divide-slate-200 border-y border-slate-200">
                {order.map((key, index) => {
                  const meta = PAGE_META[key];
                  if (!meta) return null;
                  const label = labels[key] ?? meta.defaultLabel;
                  const flash = !!savedFlash[key];
                  const isDefault = label === meta.defaultLabel;
                  return (
                    <Draggable key={key} draggableId={key} index={index}>
                      {(dragProvided, snapshot) => (
                        <li ref={dragProvided.innerRef} {...dragProvided.draggableProps}
                            data-testid={`page-visibility-row-${key}`}
                            className={`py-3 grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 bg-white ${snapshot.isDragging ? "shadow-md ring-1 ring-sky-300 rounded" : ""}`}>
                          <span {...dragProvided.dragHandleProps}
                                data-testid={`page-visibility-handle-${key}`}
                                className="text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing pl-1"
                                aria-label="Drag to reorder">
                            <GripVertical className="w-4 h-4"/>
                          </span>
                          <div className="min-w-0">
                            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
                              {meta.defaultLabel} <span className="text-slate-400 normal-case">— {meta.path}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                data-testid={`page-label-input-${key}`}
                                value={label}
                                maxLength={40}
                                placeholder={meta.defaultLabel}
                                onChange={(e) => editLabel(key, e.target.value)}
                                onBlur={(e) => commitLabel(key, e.target.value)}
                                className={`text-sm ${vis[key] ? "" : "italic text-slate-500"}`}
                              />
                              {!isDefault && (
                                <button data-testid={`page-label-reset-${key}`}
                                        onClick={() => resetLabel(key)}
                                        title={`Reset to "${meta.defaultLabel}"`}
                                        className="text-xs text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 shrink-0">
                                  <RotateCcw className="w-3.5 h-3.5"/>Reset
                                </button>
                              )}
                              {flash && (
                                <span className="text-emerald-600 text-xs inline-flex items-center gap-1">
                                  <Check className="w-3 h-3"/>saved
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {vis[key] ? <Eye className="w-4 h-4 text-slate-500"/> : <EyeOff className="w-4 h-4 text-slate-400"/>}
                            <span className="text-xs uppercase tracking-widest text-slate-500">
                              {vis[key] ? "Visible" : "Hidden"}
                            </span>
                          </div>
                          <Switch
                            data-testid={`page-visibility-toggle-${key}`}
                            checked={!!vis[key]}
                            onCheckedChange={() => toggle(key)}
                          />
                        </li>
                      )}
                    </Draggable>
                  );
                })}
                {dropProvided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </CardContent>
    </Card>
  );
}
